import { randomBytes } from 'crypto';

/**
 * Recursively adds missing keys from template into target.
 * Never overwrites existing values - only fills gaps.
 *
 * @returns List of added key paths (e.g. ["Entra", "Email.AzureAcs.Region"])
 */
export function addMissingKeys(
  target: Record<string, unknown>,
  template: Record<string, unknown>,
  prefix = '',
): string[] {
  const added: string[] = [];

  for (const key of Object.keys(template)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (!(key in target)) {
      // Key missing entirely - add the whole branch
      target[key] = template[key];
      added.push(path);
    } else if (
      isPlainObject(target[key]) &&
      isPlainObject(template[key])
    ) {
      // Both are objects - recurse to find nested missing keys
      const nested = addMissingKeys(
        target[key] as Record<string, unknown>,
        template[key] as Record<string, unknown>,
        path,
      );
      added.push(...nested);
    }
    // else: key exists with a scalar/array value - preserve client's value
  }

  return added;
}

/**
 * Replace template placeholders with project-specific values.
 * Same logic as init.ts template replacement.
 */
export function resolveTemplatePlaceholders(
  content: string,
  projectName: string,
): string {
  return content
    .replace(/\{\{ProjectName\}\}/g, projectName)
    .replace(/\{\{ProjectDomain\}\}/g, `${projectName.toLowerCase()}.app`)
    .replace(/\{\{ProjectNameLower\}\}/g, projectName.toLowerCase())
    .replace(/\{\{JwtSecret\}\}/g, randomBytes(64).toString('base64'));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Config paths the platform has DEPRECATED or RELOCATED. `ss upgrade` removes
 * them so an existing project converges to the current
 * appsettings.json.template (which mirrors the SmartStack.app binding contract).
 *
 * This is a CURATED whitelist — never "every key absent from the template" — so
 * a user's own added sections/keys are never touched. When the app drops or
 * relocates a bound section, add its OLD path here.
 */
export const OBSOLETE_PATHS: readonly (readonly string[])[] = [
  // EntraSso moved to Authentication:EntraSso; the top-level node is dead/unbound.
  ['EntraSso'],
  // Section dropped from appsettings; the API binds LicenseServerSettings defaults.
  ['LicenseServer'],
  // SmartStack:Security:RestrictExternalAppsToDataApi had no binding; whole subsection dropped.
  ['SmartStack', 'Security'],
];

/**
 * Removes deprecated config paths (default: OBSOLETE_PATHS) from `target`, in
 * place. A path that is absent is skipped. After a removal, any ancestor object
 * left empty is pruned too. Returns the dotted paths actually removed.
 */
export function removeObsoleteKeys(
  target: Record<string, unknown>,
  obsoletePaths: readonly (readonly string[])[] = OBSOLETE_PATHS,
): string[] {
  const removed: string[] = [];
  for (const path of obsoletePaths) {
    if (deleteAtPath(target, path)) {
      removed.push(path.join('.'));
    }
  }
  return removed;
}

/**
 * Deletes the value at `path` if present, walking down only through plain
 * objects. Prunes ancestor objects left empty by the deletion. Returns true iff
 * a value was removed.
 */
function deleteAtPath(root: Record<string, unknown>, path: readonly string[]): boolean {
  if (path.length === 0) return false;

  const chain: Array<{ obj: Record<string, unknown>; key: string }> = [];
  let current: Record<string, unknown> = root;
  for (let i = 0; i < path.length - 1; i++) {
    const next = current[path[i]];
    if (!isPlainObject(next)) return false; // path does not exist
    chain.push({ obj: current, key: path[i] });
    current = next;
  }

  const leaf = path[path.length - 1];
  if (!(leaf in current)) return false;
  delete current[leaf];

  // Prune now-empty ancestors, deepest first.
  for (let i = chain.length - 1; i >= 0; i--) {
    const { obj, key } = chain[i];
    const child = obj[key];
    if (isPlainObject(child) && Object.keys(child).length === 0) {
      delete obj[key];
    } else {
      break;
    }
  }

  return true;
}

/**
 * Some sections were RELOCATED, not merely dropped. Before removeObsoleteKeys
 * prunes the dead top-level node, `ss upgrade` moves a project's *configured*
 * values to the new bound location so the section survives the move instead of
 * being silently lost. Curated (like OBSOLETE_PATHS); a destination value
 * already present is never overwritten — the newer config wins.
 *
 * Currently one relocation, mirroring the SmartStack.app binding change:
 *   EntraSso (top-level)        → Authentication:EntraSso
 *   EntraSso.AllowedTenantIds   → Authentication:Microsoft:AllowedTenants
 * The tenant whitelist is FOLDED into Microsoft because EntraSsoSettings dropped
 * AllowedTenantIds (the exact same mapping the login-config CLI applies).
 *
 * Runs BEFORE addMissingKeys + removeObsoleteKeys. Returns the OLD dotted paths
 * whose values were actually relocated (empty when nothing needed moving).
 */
export function relocateLegacyKeys(target: Record<string, unknown>): string[] {
  const moved: string[] = [];

  const legacyEntra = target.EntraSso;
  if (isPlainObject(legacyEntra)) {
    const auth = ensureObject(target, 'Authentication');
    let relocatedAny = false;
    for (const [key, value] of Object.entries(legacyEntra)) {
      if (key === 'AllowedTenantIds') {
        // Folded to Authentication:Microsoft:AllowedTenants (only if unset there).
        const microsoft = ensureObject(auth, 'Microsoft');
        if (!('AllowedTenants' in microsoft)) {
          microsoft.AllowedTenants = value;
          relocatedAny = true;
        }
      } else {
        // Fill the new EntraSso location without clobbering an existing value.
        const dest = ensureObject(auth, 'EntraSso');
        if (!(key in dest)) {
          dest[key] = value;
          relocatedAny = true;
        }
      }
    }
    if (relocatedAny) moved.push('EntraSso');
  }

  return moved;
}

/** Return parent[key] as a plain object, creating an empty one if absent/not-object. */
function ensureObject(
  parent: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  const current = parent[key];
  if (isPlainObject(current)) return current;
  const created: Record<string, unknown> = {};
  parent[key] = created;
  return created;
}
